import { useState } from "react";
import type { OnboardingDraft } from "./OnboardingFlow";

const KG_PER_LB = 0.453592;
const CM_PER_IN = 2.54;

export default function Step1Basics({
  draft,
  onNext,
}: {
  draft: OnboardingDraft;
  onNext: (patch: Partial<OnboardingDraft>) => void;
}) {
  const [units, setUnits] = useState<"metric" | "imperial">("metric");
  const [name, setName] = useState(draft.name ?? "");
  const [dob, setDob] = useState(draft.dateOfBirth ?? "");
  const [weight, setWeight] = useState(
    draft.prePregnancyWeightKg ? String(draft.prePregnancyWeightKg) : ""
  );
  const [height, setHeight] = useState(draft.heightCm ? String(draft.heightCm) : "");

  const canContinue = name.trim() && dob && weight && height;

  function handleContinue() {
    const weightKg = units === "metric" ? Number(weight) : Number(weight) * KG_PER_LB;
    const heightCm = units === "metric" ? Number(height) : Number(height) * CM_PER_IN;
    onNext({ name: name.trim(), dateOfBirth: dob, prePregnancyWeightKg: weightKg, heightCm });
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold text-forest-700">Let's get started</h1>
        <p className="mt-1 text-sm text-forest-500">
          Just the basics — this helps us set nutrition targets that actually fit you.
        </p>
      </div>

      <Field label="What should we call you?">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
        />
      </Field>

      <Field label="Date of birth" hint="Nutrient targets shift slightly by age.">
        <input
          className="input"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
        />
      </Field>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink">Units</span>
        <div className="flex rounded-pill border border-line p-1">
          {(["metric", "imperial"] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUnits(u)}
              className={`h-9 rounded-pill px-4 text-sm font-medium ${
                units === u ? "bg-forest-700 text-canvas" : "text-forest-500"
              }`}
            >
              {u === "metric" ? "kg / cm" : "lb / in"}
            </button>
          ))}
        </div>
      </div>

      <Field
        label="Pre-pregnancy weight"
        hint="Used to calculate your BMI-based weight-gain range."
      >
        <input
          className="input"
          type="number"
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          placeholder={units === "metric" ? "e.g. 63" : "e.g. 139"}
        />
      </Field>

      <Field label="Height">
        <input
          className="input"
          type="number"
          inputMode="decimal"
          value={height}
          onChange={(e) => setHeight(e.target.value)}
          placeholder={units === "metric" ? "e.g. 165" : "e.g. 65"}
        />
      </Field>

      <button className="btn-primary mt-2" disabled={!canContinue} onClick={handleContinue}>
        Continue
      </button>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-forest-500">{hint}</span>}
    </label>
  );
}
